import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { getUrl } from '../../../config';
import { MODULE_LABELS } from '../ShareTemplateButton';
import { useSocket } from '../../../contexts/SocketContext';

const POLL_INTERVAL_MS = 30000;

// Aperçu en texte brut : le corps vient d'un autre utilisateur, on n'injecte pas son HTML dans la page
const toText = (html) => {
    const doc = new DOMParser().parseFromString((html || '').replace(/<br\s*\/?>|<\/(p|div)>/gi, '\n'), 'text/html');
    return doc.body.textContent || '';
};

/**
 * Affiche, un par un, les templates d'email que des collègues nous ont envoyés (acceptation ou refus).
 */
function ModalIncomingTemplate() {
    const { socket } = useSocket();
    const [pending, setPending] = useState([]);
    const [busy, setBusy] = useState(false);

    const fetchPending = useCallback(() => {
        axios.get(getUrl() + '/api/email-template-shares/pending')
            .then((response) => {
                if (response.data.status === 'success') setPending(response.data.data);
            })
            .catch(() => { /* silencieux : on réessaie au prochain passage */ });
    }, []);

    useEffect(() => {
        fetchPending();
        const timer = setInterval(fetchPending, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [fetchPending]);

    // Réception instantanée : le serveur prévient dès qu'un collègue envoie un template
    useEffect(() => {
        if (!socket) return undefined;
        socket.on('emailTemplateShared', fetchPending);
        return () => { socket.off('emailTemplateShared', fetchPending); };
    }, [socket, fetchPending]);

    const current = pending[0];

    const respond = async (accept) => {
        if (!current) return;
        setBusy(true);
        try {
            await axios.post(getUrl() + `/api/email-template-shares/${current.id}/respond`, { accept });
            setPending((prev) => prev.filter((p) => p.id !== current.id));
        } catch (error) {
            console.error('Erreur lors de la réponse au template reçu :', error);
        } finally {
            setBusy(false);
        }
    };

    if (!current) return null;

    return (
        <Dialog open fullWidth maxWidth="sm">
            <DialogTitle>
                {current.fromName} vous a envoyé une config template de mail « {MODULE_LABELS[current.module] || current.module} »
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="caption" color="textSecondary">Objet</Typography>
                <Typography sx={{ mb: 2 }}>{current.subject || '—'}</Typography>
                <Typography variant="caption" color="textSecondary">Message</Typography>
                <Box sx={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto' }}>{toText(current.body) || '—'}</Box>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
                    En acceptant, l'objet et le message de votre template remplaceront les actuels. Votre signature ne change pas.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => respond(false)} disabled={busy} color="inherit">Refuser</Button>
                <Button onClick={() => respond(true)} disabled={busy} variant="contained">Accepter</Button>
            </DialogActions>
        </Dialog>
    );
}

export default ModalIncomingTemplate;
